import React, { useState, useEffect } from 'react';
import { promocionService } from 'services/promocionService';
import { catalogoService } from 'services/usuarioService';
import { EstadoPromocionResponse, ESTADO_PROMO_LABEL, ESTADO_PROMO_COLOR } from 'types/promocion.types';
import { AñoEscolar, Grado, Seccion } from 'types/usuario.types';
import { exportarExcel } from 'utils/reporteExport';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// OrdenMeritoPage — Orden de Mérito por sección
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const MedallaIcon: React.FC<{ puesto: number }> = ({ puesto }) => {
  if (puesto === 1) return <span className="text-lg" title="Primer puesto" style={{ color: '#f59e0b' }}>🥇</span>;
  if (puesto === 2) return <span className="text-lg" title="Segundo puesto" style={{ color: '#94a3b8' }}>🥈</span>;
  if (puesto === 3) return <span className="text-lg" title="Tercer puesto" style={{ color: '#b45309' }}>🥉</span>;
  return <span className="text-sm font-bold text-slate-500">{puesto}</span>;
};

const OrdenMeritoPage: React.FC = () => {
  const [anios, setAnios] = useState<AñoEscolar[]>([]);
  const [grados, setGrados] = useState<Grado[]>([]);
  const [secciones, setSecciones] = useState<Seccion[]>([]);
  const [anioId, setAnioId] = useState<number | ''>('');
  const [nivel, setNivel] = useState('');
  const [gradoId, setGradoId] = useState<number | ''>('');
  const [seccionId, setSeccionId] = useState<number | ''>('');
  const [cargandoCatalogos, setCargandoCatalogos] = useState(true);

  const [orden, setOrden] = useState<EstadoPromocionResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cargar = async () => {
      setCargandoCatalogos(true);
      try {
        const [a, g, s] = await Promise.all([
          catalogoService.getAñosEscolares(),
          catalogoService.getGrados(),
          catalogoService.getSecciones(),
        ]);
        setAnios(a);
        setGrados(g);
        setSecciones(s);
        if (a.length > 0) setAnioId(a[0].idAnioEscolar);
      } finally {
        setCargandoCatalogos(false);
      }
    };
    cargar();
  }, []);

  const niveles = Array.from(new Set(grados.map(g => g.nivel))).sort();
  const gradosFiltrados = nivel ? grados.filter(g => g.nivel === nivel) : grados;
  const seccionesFiltradas = secciones;

  const handleConsultar = async () => {
    if (anioId === '' || gradoId === '' || seccionId === '') return;
    setLoading(true);
    setError(null);
    try {
      const data = await promocionService.getOrdenMerito({
        gradoId: Number(gradoId),
        seccionId: Number(seccionId),
        anioEscolarId: Number(anioId),
      });
      setOrden(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al consultar orden de mérito');
    } finally {
      setLoading(false);
    }
  };

  const handleExportar = () => {
    if (orden.length === 0) return;
    const gradoNombre = grados.find(g => g.idGrado === Number(gradoId))?.nombre ?? '';
    const seccionNombre = secciones.find(s => s.idSeccion === Number(seccionId))?.nombre ?? '';
    const anioLabel = anios.find(a => a.idAnioEscolar === Number(anioId))?.anio ?? '';

    exportarExcel(
      `orden_merito_${gradoNombre}_${seccionNombre}_${anioLabel}`,
      [{
        nombre: 'Orden de Mérito',
        filas: [
          ['Puesto', 'Apellidos', 'Nombres', 'DNI', 'Promedio Anual', 'Áreas Desap.', 'Estado'],
          ...orden.map((e, idx) => [
            e.puesto ?? (idx + 1),
            e.estudianteApellidos,
            e.estudianteNombres,
            e.estudianteDni,
            e.promedioAnual ?? '',
            e.areasDesaprobadas,
            ESTADO_PROMO_LABEL[e.estado],
          ]),
        ],
      }],
    );
  };

  if (cargandoCatalogos) {
    return <div className="flex justify-center py-20"><SpinnerIcon /></div>;
  }

  return (
    <div className="space-y-5">
      {/* Cabecera */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold">Orden de Mérito</h1>
        <p className="text-blue-200 mt-1">Ranking académico por sección y año escolar</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Año Escolar</label>
            <select
              value={anioId}
              onChange={e => setAnioId(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Seleccionar año</option>
              {anios.map(a => (
                <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Nivel</label>
            <select
              value={nivel}
              onChange={e => { setNivel(e.target.value); setGradoId(''); setSeccionId(''); }}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los niveles</option>
              {niveles.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Grado</label>
            <select
              value={gradoId}
              onChange={e => { setGradoId(e.target.value === '' ? '' : Number(e.target.value)); setSeccionId(''); }}
              disabled={!nivel}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="">Seleccionar grado</option>
              {gradosFiltrados.map(g => (
                <option key={g.idGrado} value={g.idGrado}>{g.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Sección</label>
            <select
              value={seccionId}
              onChange={e => setSeccionId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={gradoId === ''}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="">Seleccionar sección</option>
              {seccionesFiltradas.map(s => (
                <option key={s.idSeccion} value={s.idSeccion}>{s.nombre}</option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={handleConsultar}
          disabled={loading || anioId === '' || gradoId === '' || seccionId === ''}
          className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-xl px-5 py-2.5 transition-colors disabled:opacity-60"
        >
          {loading ? <SpinnerIcon /> : (
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"/>
            </svg>
          )}
          Consultar
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">{error}</div>
      )}

      {/* Tabla resultados */}
      {orden.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
            <div>
              <h2 className="font-bold text-slate-800">Resultados</h2>
              <p className="text-slate-400 text-xs mt-0.5">{orden.length} estudiantes</p>
            </div>
            <button
              onClick={handleExportar}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl px-4 py-2 transition-colors"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M10 3a.75.75 0 0 1 .75.75v10.638l3.96-4.158a.75.75 0 1 1 1.08 1.04l-5.25 5.5a.75.75 0 0 1-1.08 0l-5.25-5.5a.75.75 0 1 1 1.08-1.04l3.96 4.158V3.75A.75.75 0 0 1 10 3Z" clipRule="evenodd"/>
              </svg>
              Exportar Excel
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  {['Puesto', 'Apellidos', 'Nombres', 'DNI', 'Promedio Anual', 'Estado'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orden.map((e, idx) => {
                  const puesto = e.puesto ?? (idx + 1);
                  const esTop3 = puesto <= 3;
                  return (
                    <tr key={e.idEstadoPromocion}
                      className={`hover:bg-slate-50 transition-colors ${esTop3 ? 'bg-amber-50/40' : ''}`}
                    >
                      <td className="px-4 py-3 text-center w-16">
                        <MedallaIcon puesto={puesto} />
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-slate-800">{e.estudianteApellidos}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{e.estudianteNombres}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{e.estudianteDni}</td>
                      <td className="px-4 py-3 text-center">
                        {e.promedioAnual !== null ? (
                          <span className={`text-sm font-bold ${e.promedioAnual >= 11 ? 'text-green-700' : 'text-red-600'}`}>
                            {e.promedioAnual.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${ESTADO_PROMO_COLOR[e.estado]}`}>
                          {ESTADO_PROMO_LABEL[e.estado]}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && orden.length === 0 && !error && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-14 text-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-12 h-12 text-slate-200 mx-auto mb-3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
          </svg>
          <p className="text-slate-400 text-sm font-medium">Selecciona los filtros y haz clic en Consultar</p>
        </div>
      )}
    </div>
  );
};

export default OrdenMeritoPage;
